import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { triplePointsEndDate } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import TriplePointTakeoverCampaignPill from './TriplePointTakeoverCampaignPill'

interface Props {
  mobileCompact?: boolean
  textColour?: App.Design.ContentColour
  viewOffersTo?: string
}

/**
 * Shared subtitle
 */
function TriplePointTakeoverCampaignSubtitle(props: Props) {
  const {
    mobileCompact,
    textColour: textColourOverride,
    viewOffersTo,
  } = props
  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const textColour = textColourOverride ?? `tier-${tier}-contrast`

  const formattedEndDate = useMemo(() => {
    const endDate = new Date(triplePointsEndDate)
    if (Number.isNaN(endDate.getTime())) return undefined

    return intl.formatDate(endDate, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }, [intl])

  const viewAllOffersText = intl.formatMessage({
    id: 'uiS77hbzDzRjhnTehe9M',
    defaultMessage: 'View all offers',
    description: 'Link text to view all triple points offers in the trip planner',
  })

  const renderViewOffersLink = useCallback((chunks: ReactNode) => {
    return <TextLink key="view-all-offers-link" to={viewOffersTo} wrap="no-wrap">{chunks}</TextLink>
  }, [viewOffersTo])

  const defaultPill = <TriplePointTakeoverCampaignPill />
  let subtitle: ReactNode

  if (!formattedEndDate) {
    if (viewOffersTo) {
      subtitle = <FormattedMessage
        id="dekrdn_8PTXITD2_ltZX"
        defaultMessage="Look for {pill} on select packages! Hurry, limited time only. <link>View all offers</link>"
        description="Body text directing users to look for triple points pill with limited time urgency and view all offers link"
        values={{
          pill: defaultPill,
          link: renderViewOffersLink,
        }}
      />
    } else {
      subtitle = <FormattedMessage
        id="eriI4lOs8CoS1t6O0VMD"
        defaultMessage="Look for {pill} on select packages! Hurry, limited time only."
        description="Body text directing users to look for triple points pill with limited time urgency"
        values={{
          pill: defaultPill,
        }}
      />
    }
  } else if (viewOffersTo) {
    subtitle = <FormattedMessage
      id="YOpJHLKxVcjpA3SunZ4n"
      defaultMessage="Look for {pill} on select packages! Hurry, valid until {date}. <link>View all offers</link>"
      description="Body text directing users to look for triple points pill with campaign end date and view all offers link"
      values={{
        pill: defaultPill,
        date: formattedEndDate,
        link: renderViewOffersLink,
      }}
    />
  } else {
    subtitle = <FormattedMessage
      id="jJP7ENZH9tM8dAFcNbM1"
      defaultMessage="Look for {pill} on select packages! Hurry, valid until {date}."
      description="Body text directing users to look for triple points pill with the campaign end date on select packages"
      values={{
        pill: defaultPill,
        date: formattedEndDate,
      }}
    />
  }

  if (!mobileCompact) {
    return <BodyText variant="large" colour={textColour}>
      {subtitle}
    </BodyText>
  }

  return <>
    <CSSBreakpoint max="mobile">
      <Group direction="horizontal" gap={8} wrap="wrap">
        <BodyText variant="large" colour={textColour}>
          <FormattedMessage
            id="tpVxc4gzulVYQo-A_0z7"
            defaultMessage="Look for {pill}"
            description="Mobile text directing users to look for triple points pill on offers"
            values={{
              pill: defaultPill,
            }}
          />
        </BodyText>
        {viewOffersTo && <TextLink
          size="large"
          to={viewOffersTo}
          wrap="no-wrap"
          weight="regular"
        >
          {viewAllOffersText}
        </TextLink>}
      </Group>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <BodyText variant="large" colour={textColour}>
        {subtitle}
      </BodyText>
    </CSSBreakpoint>
  </>
}

export default TriplePointTakeoverCampaignSubtitle
